import { useMemo, useState } from "react";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../Pagination/PageSizeControls";
import type { IPaginationProps } from "../../Pagination/types";
import type { ITablePagination } from "../types";

type Props<Item> = {
  items: Item[];
  pagination?: boolean | ITablePagination;
};

type UsePaginationControllerReturn<Item> = {
  paginatedItems: Item[];
  paginationConfig: IPaginationProps | undefined;
  resetPage: () => void;
};

export function usePaginationController<Item>({
  items,
  pagination,
}: Props<Item>): UsePaginationControllerReturn<Item> {
  const [internalPage, setInternalPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(
    DEFAULT_PAGE_SIZE_OPTIONS[0],
  );

  return useMemo(() => {
    const minPageSize = DEFAULT_PAGE_SIZE_OPTIONS[0];

    if (!pagination) {
      return {
        paginatedItems: items,
        paginationConfig: undefined,
        resetPage: () => {},
      };
    }

    if (pagination === true) {
      if (items.length <= minPageSize) {
        return {
          paginatedItems: items,
          paginationConfig: undefined,
          resetPage: () => setInternalPage(1),
        };
      }

      const startIndex = (internalPage - 1) * internalPageSize;
      const endIndex = startIndex + internalPageSize;
      const slicedItems = items.slice(startIndex, endIndex);

      const config: IPaginationProps = {
        page: internalPage,
        pageSize: internalPageSize,
        totalItems: items.length,
        onPageChange: setInternalPage,
        onPageSizeChange: (newPageSize: number) => {
          setInternalPageSize(newPageSize);
          setInternalPage(1);
        },
      };

      return {
        paginatedItems: slicedItems,
        paginationConfig: config,
        resetPage: () => setInternalPage(1),
      };
    }

    const shouldSlice = !pagination.server;
    if (shouldSlice) {
      if (pagination.totalItems <= minPageSize) {
        return {
          paginatedItems: items,
          paginationConfig: undefined,
          resetPage: () => {},
        };
      }

      const startIndex = (pagination.page - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const slicedItems = items.slice(startIndex, endIndex);

      const { server, ...paginationProps } = pagination;

      return {
        paginatedItems: slicedItems,
        paginationConfig: paginationProps,
        resetPage: () => {},
      };
    }

    if (pagination.totalItems <= minPageSize) {
      return {
        paginatedItems: items,
        paginationConfig: undefined,
        resetPage: () => {},
      };
    }

    const { server, ...paginationProps } = pagination;

    return {
      paginatedItems: items,
      paginationConfig: paginationProps,
      resetPage: () => {},
    };
  }, [items, pagination, internalPage, internalPageSize]);
}
